"use client";

import NextLink from "next/link";
import { useLocale } from "next-intl";
import type { ComponentProps } from "react";
import { localeHref } from "@/lib/locale-href";

/**
 * Drop-in replacement for `next/link` that points at the CURRENT locale's URL.
 *
 * Why this exists: every internal href in the app was unprefixed, so the
 * destination locale was decided by the `NEXT_LOCALE` cookie in middleware, not
 * by the page you were reading. Two consequences — a stale `vi` cookie bounced
 * an EN reader to `/vi` on any nav click, and (worse) a crawler on `/vi/*` saw
 * ZERO links to other VI pages: all 15 internal links pointed at EN URLs, so
 * the whole VI tree was an SEO orphan reachable only via hreflang + sitemap.
 *
 * Why not next-intl's `Link` from `createNavigation`: its BaseLink calls
 * `usePathname()` unconditionally (only to sync the locale cookie in onClick —
 * a feature that needs an explicit `locale` prop we never pass). On a `[slug]`
 * route the path is unknowable at prerender, so it makes the static Nav shell
 * dynamic and fails the build. `useLocale()` is context-based and static-safe,
 * so this stays prerenderable. See `locale-href.ts` for the full note.
 *
 * `"use client"` is required for `useLocale()`, but costs nothing: `next/link`
 * is a client component anyway, and children are still server-rendered and
 * passed through as props.
 *
 * Pass UNPREFIXED hrefs (`/about`, not `/vi/about`) — the prefix is added here.
 */
export default function LocaleLink({ href, ...rest }: ComponentProps<typeof NextLink>) {
  const locale = useLocale();

  // `href` may be a string or a UrlObject ({ pathname, query }) — the homepage
  // service shortcuts use the latter to omit absent query keys.
  const localized =
    typeof href === "string"
      ? localeHref(href, locale)
      : { ...href, ...(href.pathname ? { pathname: localeHref(href.pathname, locale) } : {}) };

  return <NextLink href={localized} {...rest} />;
}
